/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import React from 'react';
import { WMDefaultStyleName } from '@opengeoweb/webmap';
import type { Dimension } from '@opengeoweb/webmap';
import { GeoTIFFLayer } from './GeoTIFFLayer';
import type { OpenLayersFeatureLayerProps } from './OpenLayersFeatureLayer';
import {
  useStacItems,
  useStacCollection,
} from '../../../geotiff-layer-api/hooks';
import { stacColormapToOlStyle } from '../../../geotiff-layer-api/utils/stacColormapToOlStyle';
import { useGetStacGeoTIFFLayerInstance } from '../../../geotiff-layer-api/utils/useGetStacGeoTIFFLayerInstance';
import { getDimensionParamsForWMS } from '../utils/makeDimValuesToPrefetch';
import type { OnInitializeLayerProps } from '../utils/types';

interface TimeAwareGeoTIFFLayerProps extends OpenLayersFeatureLayerProps {
  layerId?: string;
  stacCollectionUrl: string;
  layerName: string;
  styleName?: string;
  dimensions?: Dimension[];
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  opacity?: number;
  visible?: boolean;
}

const resolvePreferredStyleName = (
  styleNames: string[],
  currentStyle?: string,
): string => {
  if (styleNames.length === 0) {
    return WMDefaultStyleName;
  }

  if (currentStyle && styleNames.includes(currentStyle)) {
    return currentStyle;
  }

  if (styleNames.includes(WMDefaultStyleName)) {
    return WMDefaultStyleName;
  }

  return styleNames[0];
};

export const TimeAwareGeoTIFFLayer: React.FC<TimeAwareGeoTIFFLayerProps> = ({
  stacCollectionUrl,
  layerName,
  style,
  styleName,
  zIndex,
  opacity,
  dimensions = [],
  layerId,
  visible = true,
  onInitializeLayer,
}) => {
  const wmLayer = useGetStacGeoTIFFLayerInstance(
    stacCollectionUrl,
    layerName,
    layerId,
    onInitializeLayer,
  );

  const { data: collectionData } = useStacCollection(stacCollectionUrl);

  const effectiveStyleName = React.useMemo(() => {
    const renderNames = Object.keys(collectionData?.renders || {});
    return resolvePreferredStyleName(renderNames, styleName);
  }, [collectionData, styleName]);

  const geotiffStyle = React.useMemo(() => {
    const renderEntry = collectionData?.renders?.[effectiveStyleName];
    if (renderEntry?.colormap) {
      return stacColormapToOlStyle(renderEntry.colormap);
    }

    return undefined;
  }, [effectiveStyleName, collectionData]);

  const shouldNormalizeGeoTiff =
    effectiveStyleName === WMDefaultStyleName &&
    !collectionData?.renders?.[effectiveStyleName]?.colormap;

  const dimensionsValues = getDimensionParamsForWMS(dimensions, wmLayer);
  const timeValue = dimensionsValues?.['TIME'];

  const [startDate, endDate] = React.useMemo(() => {
    if (!timeValue) {
      return [undefined, undefined];
    }
    const date = new Date(timeValue);
    const start = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0,
        0,
        0,
        0,
      ),
    );
    const end = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        23,
        59,
        59,
        999,
      ),
    );
    return [start.toISOString(), end.toISOString()];
  }, [timeValue]);

  const { data: items, isLoading } = useStacItems(
    stacCollectionUrl,
    startDate,
    endDate,
  );

  // Find the closest item index for the selected time
  const closestIndex = React.useMemo(() => {
    if (!items || !timeValue) {
      return -1;
    }
    const target = new Date(timeValue).getTime();
    let minDiff = Infinity;
    let idx = -1;
    items.forEach((item, i) => {
      const wkt2 = item.properties['proj:wkt2'];

      if (wkt2) {
        registerProjection(wkt2);
      }
      const diff = Math.abs(
        new Date(item.properties.datetime!).getTime() - target,
      );
      if (diff < minDiff) {
        minDiff = diff;
        idx = i;
      }
    });
    return idx;
  }, [items, timeValue]);

  const closestItem =
    closestIndex >= 0 && items ? items[closestIndex] : undefined;

  if (!items || isLoading) {
    return null;
  }

  if (!closestItem) {
    return null;
  }

  return (
    <GeoTIFFLayer
      geotiffStyle={geotiffStyle}
      key={`${closestItem.id}-${effectiveStyleName}`}
      layerName={closestItem.id}
      layerOptions={{
        opacity,
        zIndex,
      }}
      layerProps={{
        enabled: visible,
      }}
      normalize={shouldNormalizeGeoTiff}
      projection={closestItem.properties['proj:wkt2']}
      url={closestItem.assets['data'].href}
    />
  );
};

const registerProjection = (wkt2: string): void => {
  if (!proj4.defs(wkt2)) {
    proj4.defs(wkt2, wkt2);
    register(proj4);
  }
};
